<template>
  <div class="mobile-container">
    <BaseInput :label="$t('MobileAutocompleteSelect.label.phoneCountry') + `*`">
      <AutocompleteSelect
        v-model:autocomplete="form.autocomplete"
        :type="'text'"
        :items="form.sortedCountries"
        :hasIcon="true"
        autocompleteType="phoneCountry"
      />
    </BaseInput>
    <BaseInput :label="$t('MobileField.label.mobileNumber') + `*`" hasIcon="true" :errors="props.errors">
      <MailCircle />
      <input
        type="mobile"
        id="mobile"
        :class="{ hasErrors: props.errors }"
        name="mobile"
        :placeholder="$t('MobileField.label.mobileNumber')"
        autocomplete="off"
        :value="mobile"
        @keyup="$emit('update:mobile', $event.target.value)"
        @blur="$emit('update:mobile', $event.target.value)"
      />
    </BaseInput>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import BaseInput from "@/components/ui/inputs/BaseInput.vue";
import MailCircle from "@/components/icons/MailCircle.vue";

import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";
import { useGlobalStore } from "@/stores/globalStore";

const globalStore = useGlobalStore();
const props = defineProps({
  mobile: {
    type: String,
    default: "",
  },
  countries: {
    type: Array,
    required: true,
  },
  errors: {
    type: String,
    default: null,
  },
  defaultValues: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(["update:mobile", "dropdownValueUpdate"]);

const form = ref({
  autocomplete: "",
  sortedCountries: [],
});

const initSortCountries = () => {
  const pluckCountryCodes = props.countries.map((country) => country.code);

  form.value.sortedCountries = getCountries()
    .filter((phoneCountryCode) => pluckCountryCodes.includes(phoneCountryCode))
    .map((phoneCountryCode) => {
      const country = props.countries.find(
        (countrySearch) => countrySearch.code === phoneCountryCode
      );
      return {
        phoneCountry: country.code,
        code: country.code,
        ...{
          internationalCode: getCountryCallingCode(phoneCountryCode),
          name: country.name,
        },
      };
    })
    // eslint-disable-next-line no-nested-ternary
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  globalStore.setCountries(form.value.sortedCountries);
};

const findCountryData = (val) =>
  form.value.sortedCountries.find((country) => country.name === val);

watch(
  () => props.countries,
  (val) => {
    if (val.length > 0) {
      initSortCountries();
    }
  },
  {
    deep: true,
  }
);
watch(
  () => form.value.autocomplete,
  (val) => {
    const foundCountry = findCountryData(val);
    if (foundCountry) {
      form.value.autocomplete = `${foundCountry.name} (+${foundCountry.internationalCode})`;
      emit("dropdownValueUpdate", foundCountry);
    }
  },
  { deep: true }
);

watch(
  () => props.defaultValues,
  () => {
    form.value.mobile = props.defaultValues.mobile;
    form.value.autocomplete = `${props.defaultValues.mobileCountry} (${props.defaultValues.internationalCode})`;
  },
  { deep: true, immediate: true }
);
</script>
<style lang="scss">
.mobile-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2.4rem;
  gap: 1rem;

  @media screen and (max-width: $screen-md) {
    flex-direction: column;
    align-items: unset;
  }

  .base-input {
    margin-bottom: 0;
    flex: 1;
  }

  .autocomplete-select__select-field
    .autocomplete-container
    .autocomplete-item {
    display: flex;
  }
}
</style>
